// Office settings (admin/office). Account + company details + the live Schedule of
// Fees, plus (admin only) the editable CPI rate used for renewal pricing and a live
// view of which integrations are connected. Integration credentials live on the
// server; OAuth providers (Google, Xero) can be (re)connected from here, the rest
// are configured in the host service config.
//
// SESSION SC7B (the calm screens, part 2): the page is a short list of sections (src/lib/settingsSections.ts), ONE
// on screen at a time (a section mounted when first opened, then kept hidden), with the section in the address (/office/settings/email). The
// Personal / Office / Admin tab strip is gone. Every card below is the same component, with the same admin-only
// gate it always had; SectionCards decides which cards sit together.
import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ApiError, api } from "../../lib/api";
import type { SmsLogItem, TenantSettings, IntegrationStatus, BankDetails, GeocodeStatus, AutomationSettings } from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { ConfirmButton } from "../../components/ConfirmButton";
import { useSingleTimeout } from "../../lib/useSingleTimeout";
import { friendlyApiError } from "../../lib/friendlyApiError";
import { initialMobileLayout } from "../../lib/mobileLayout";
import { MobileCardList, MobileListCard } from "../../components/MobileListCard";
// The client mirror of the server's trusted-sender rule (see that module's header for why it exists
// and why the server is still the authority).
import { TRUSTED_SENDER_PATTERN, KB_TRUSTED_SENDERS_MAX } from "../../lib/trustedSenders";
// Session ARZ: how a subcontractor's Xero payable contact is set up (its own file, admin only).
import { SubcontractorXeroSettingsCard } from "./SubcontractorXeroSettingsCard";
// Session ASQ (Stream G, Item 5): the privacy notice (its own file, admin only).
import { PrivacyNoticeSettingsCard } from "./PrivacyNoticeSettingsCard";
import { EleAddressesSettingsCard } from "./EleAddressesSettingsCard";
import { BackupStatusCard } from "./BackupStatusCard";
import { CompanySignatureSettingsCard } from "./CompanySignatureSettingsCard";
import { LateReplyWordingSettingsCard } from "./LateReplyWordingSettingsCard";
import { SubcontractorDocumentsSettingsCard } from "./SubcontractorDocumentsSettingsCard";
import { StandardRateCardSettingsCard } from "./StandardRateCardSettingsCard";
import { RecommendedExaminerSettingsCard } from "./RecommendedExaminerSettingsCard";
import { EmailDeliverabilityCard } from "./EmailDeliverabilityCard";
import { ChaseSettingsCard } from "./ChaseSettingsCard";
import { ReportSchedulesSettingsCard } from "./ReportSchedulesSettingsCard";
import { CalendarSyncSection } from "./CalendarSyncSection";
// Disconnecting Google or Xero confirms in two steps (Session ASB).
import { ConfirmDeleteModal } from "../../components/ConfirmDeleteModal";
import { Button } from "../../components/Button";
import { Loader } from "../../components/Loader";
import { PageHeader } from "../../components/PageHeader";
import { StatusPill } from "../../components/StatusPill";
import { ConnectorInstructionsCard } from "../../components/ConnectorInstructionsCard";
import { formatDateTime } from "../../lib/format";
import { DEFAULT_SETTINGS_SECTION, SETTINGS_SECTIONS, findSettingsSection, settingsSectionPath } from "../../lib/settingsSections";
import { SettingsSummaryProvider, useReportSettingsSummary } from "./settingsSummary";

const ROLE_LABEL: Record<string, string> = { admin: "Administrator", office: "Office", engineer: "Engineer" };

// The registered address (Companies House) is compliance-locked and shown read-only -- it
// must match CLAUDE.md exactly and is never editable in the UI.
const REGISTERED_ADDRESS = "Unit 82a James Carter Road, Mildenhall, Bury St Edmunds, IP28 7DE";

// Fraction (0.015) -> percent string ("1.5"), trimming trailing zeros.
function pct(fraction: number | null): string {
  if (fraction == null) return "n/a";
  const p = fraction * 100;
  return `${Number.isInteger(p) ? p : Number(p.toFixed(2))}%`;
}

export function SettingsPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const { section: sectionParam } = useParams<{ section?: string }>();
  const navigate = useNavigate();
  // Refresh (session prompt, 14/08/26): every card on this page fetches independently on its own mount
  // effect, so there is no single request to re-issue. Bumping this and threading it into each card's own
  // effect dependency array re-runs every one of those effects, the same mechanism
  // ReportingDashboardPage's "Refresh re-reads every panel" button already uses. Since SC7B only the sections
  // the person has OPENED are mounted, so the one click re-reads every card that has been loaded.
  const [refreshKey, setRefreshKey] = useState(0);
  // Each section's one-line state, reported by a card that already fetched it (no request of its own); the header shows the open one's.
  const [summaries, setSummaries] = useState<Record<string, string | null>>({});

  // SETTINGS AS SECTIONS (Session SC7B, the calm screens part 2; the Director: Settings "is mental"). Before this
  // there were three tabs (Personal, Office, Admin) holding about fifteen cards between them, ALL mounted and
  // hidden by a CSS class, so the page fetched everything on every visit. Now it is a short list of sections,
  // ONE open at a time and ONLY its cards mounted, with the section in the address (/office/settings/email) so a
  // bookmark, a notice link and the menu search land on the right one. Each card is the same component with the
  // same admin-only rule it always had; a section is simply which cards are shown together. The bare address
  // opens the first section ("Your account"), and an address that names no section shows it too.
  const requested = findSettingsSection(sectionParam);
  const active = requested ?? SETTINGS_SECTIONS[0]!;
  const unknownSection = sectionParam !== undefined && requested === null;

  useEffect(() => {
    if (unknownSection) navigate(settingsSectionPath(DEFAULT_SETTINGS_SECTION), { replace: true });
  }, [unknownSection, navigate]);

  // VISITED SECTIONS STAY MOUNTED (SC7B review, finding 1). A section is mounted the first time it is opened and then kept (hidden by
  // the existing "hidden" class) for the rest of the visit, so something typed into a card (a CPI override, a signature edit, a
  // supplier list) survives moving to another section and back, exactly as it did when every panel was mounted. Only the first
  // section is mounted on load; an unvisited section is never mounted. The trade-off: fetches and memory grow with the sections
  // opened, never beyond what the old page loaded at once.
  const [visited, setVisited] = useState<string[]>([active.id]);
  useEffect(() => {
    setVisited((v) => (v.includes(active.id) ? v : [...v, active.id]));
  }, [active.id]);
  const mounted = visited.includes(active.id) ? visited : [...visited, active.id];
  const summary = summaries[active.id] ?? null;

  return (
    // .ele-settings scopes the Session ACG locked form treatment (quiet inputs with the
    // purple focus ring, small uppercase section headings over a soft purple rule) to this
    // page via CSS -- see index.css.
    <div className="ele-settings">
      <PageHeader
        className="mb-6"
        title="Settings"
        subtitle="Your account, company details and shared documents."
        actions={
          <Button variant="secondary" onClick={() => setRefreshKey((k) => k + 1)}>
            Refresh
          </Button>
        }
      />

      <div className="md:grid md:grid-cols-[13rem_minmax(0,1fr)] md:items-start md:gap-6">
        <div>
          {/* A phone gets a dropdown (a 44px target), everything wider gets the list. Both are links to
              the same addresses, so the open section is always the one the address names. */}
          <label className="mb-4 block md:hidden">
            <span className="mb-1 block font-menu text-xs uppercase tracking-wide text-ele-body">Section</span>
            <select
              aria-label="Settings section"
              value={active.id}
              onChange={(e) => navigate(settingsSectionPath(e.target.value))}
              className="min-h-11 w-full rounded-ele border border-ele-purple-accent bg-ele-field px-3 py-2 text-sm text-ele-strong outline-none focus:border-ele-green"
            >
              {SETTINGS_SECTIONS.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.label}
                </option>
              ))}
            </select>
          </label>
          <nav aria-label="Settings sections" className="mb-5 max-md:hidden md:mb-0">
            <ul className="space-y-1">
              {SETTINGS_SECTIONS.map((s) => {
                const isActive = s.id === active.id;
                return (
                  <li key={s.id}>
                    <Link
                      to={settingsSectionPath(s.id)}
                      aria-current={isActive ? "page" : undefined}
                      className={`block rounded-ele border-0 px-3 py-2 font-heading text-sm tracking-wide no-underline transition ${
                        isActive ? "bg-ele-field text-ele-green" : "text-ele-body hover:bg-ele-field hover:text-ele-strong"
                      }`}
                    >
                      {s.label}
                    </Link>
                  </li>
                );
              })}
            </ul>
          </nav>
        </div>

        <div className="min-w-0" data-settings-section={active.id}>
          <div className="mb-1">
            <p className="font-heading text-xl text-ele-strong">{active.label}</p>
            {summary && (
              <p role="status" className="mt-1 text-sm text-ele-body" data-settings-summary="">
                {summary}
              </p>
            )}
          </div>
          {mounted.map((id) => (
            <div key={id} className={id === active.id ? "" : "hidden"} data-settings-panel={id}>
              <SettingsSummaryProvider report={(text) => setSummaries((m) => (m[id] === text ? m : { ...m, [id]: text }))}>
                <SectionCards id={id} isAdmin={isAdmin} refreshKey={refreshKey} />
              </SettingsSummaryProvider>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// The cards of one section. Every card appears in exactly ONE section for a login (the calendar sync, which used
// to be mounted on the Office tab for an office login and on the Admin tab for an admin, is in Integrations for
// both), and an admin-only card is mounted only for an admin, exactly the gates each card carried before.
function SectionCards({ id, isAdmin, refreshKey }: { id: string; isAdmin: boolean; refreshKey: number }) {
  const { user } = useAuth();
  switch (id) {
    case "your-account":
      return (
        // Session ANQ: only "Your account" and "Documents" are genuinely short and well matched, so only they
        // are paired in a grid; everything longer is its own card in its own section.
        <div className="mt-4 grid gap-5 lg:grid-cols-2">
          <section className="rounded-ele border border-ele-purple-accent bg-ele-surface p-5">
            <h2 className="mb-3 font-heading text-lg text-ele-blue">Your account</h2>
            <dl className="space-y-1 text-sm">
              <div className="flex justify-between gap-3"><dt className="text-ele-body">Name</dt><dd className="text-ele-strong">{user?.displayName ?? "n/a"}</dd></div>
              <div className="flex justify-between gap-3"><dt className="text-ele-body">Email</dt><dd className="text-ele-strong">{user?.email ?? "n/a"}</dd></div>
              <div className="flex justify-between gap-3"><dt className="text-ele-body">Role</dt><dd className="text-ele-strong">{user ? (ROLE_LABEL[user.role] ?? user.role) : "n/a"}</dd></div>
            </dl>
          </section>

          <section className="rounded-ele border border-ele-purple-accent bg-ele-surface p-5">
            <h2 className="mb-3 font-heading text-lg text-ele-blue">Documents</h2>
            <p className="mb-3 text-sm text-ele-body">The Schedule of Fees is generated from the live pricing rates.</p>
            <a
              href={api.documents.scheduleOfFeesHref()}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block rounded-ele bg-ele-green px-4 py-2 font-heading text-sm tracking-wide text-ele-bg"
            >
              Download Schedule of Fees
            </a>
          </section>
        </div>
      );
    case "company":
      return (
        <>
          <section className="mt-4 rounded-ele border border-ele-purple-accent bg-ele-surface p-5">
            <h2 className="mb-3 font-heading text-lg text-ele-blue">Company</h2>
            {/* Capped to a readable measure now that the card spans the full width: a label/value
                row stretched across the whole page reads as a wall of whitespace, not a form. */}
            <div className="max-w-md">
              <dl className="space-y-1 text-sm">
                <div className="flex justify-between gap-3"><dt className="text-ele-body">Company</dt><dd className="text-ele-strong">East London Elevators Ltd</dd></div>
                <div className="flex justify-between gap-3"><dt className="text-ele-body">Company number</dt><dd className="text-ele-strong">15429089</dd></div>
                <div className="flex justify-between gap-3"><dt className="text-ele-body">VAT</dt><dd className="text-ele-strong">GB493339461</dd></div>
                <div className="flex justify-between gap-3"><dt className="text-ele-body">Phone</dt><dd className="text-ele-strong">0203 576 4760</dd></div>
                <div className="flex justify-between gap-3"><dt className="text-ele-body">Email</dt><dd className="text-ele-strong">info@eastlondonelevators.co.uk</dd></div>
                {/* Registered address: compliance-locked, display only, never editable here (CLAUDE.md). */}
                <div className="flex justify-between gap-3"><dt className="text-ele-body">Registered address</dt><dd className="text-right text-ele-strong">{REGISTERED_ADDRESS}</dd></div>
              </dl>
              <BankDetailsSection refreshKey={refreshKey} />
            </div>
          </section>
          <RecommendedExaminerSettingsCard refreshKey={refreshKey} />
        </>
      );
    case "email":
      return (
        <>
          {/* Session ATZ (Item 6): ELE's own email addresses, the one list the email system reads. office and admin. */}
          <EleAddressesSettingsCard refreshKey={refreshKey} />
          {/* Session AUA (DE-3): the one company signature every drafted reply carries. office and admin. */}
          <CompanySignatureSettingsCard refreshKey={refreshKey} />
          <LateReplyWordingSettingsCard refreshKey={refreshKey} />
          {/* Session AUD (Item 5): SPF, DKIM and DMARC read live from DNS on Check now. office and admin, read-only. */}
          <EmailDeliverabilityCard />
          <RecentSms refreshKey={refreshKey} />
        </>
      );
    case "chasing":
      return (
        <>
          {/* Session AUH (Phase 7): the chase ladders, the supplier list and (admin only) the debt recovery contact. */}
          <ChaseSettingsCard refreshKey={refreshKey} />
          {/* The admin-only money and pricing settings (CPI, payment runway, quote validity, labour cost, call
              survey delay, inbox chase threshold, knowledge base senders): ONE card with seven sub-sections, each with
              its own Save, kept whole (SC7B, D-SC7B-1). */}
          {isAdmin && <CompanySettings refreshKey={refreshKey} />}
        </>
      );
    case "integrations":
      return (
        <>
          {isAdmin && <IntegrationsSection refreshKey={refreshKey} />}
          {/* Session ATY: calendar sync. An office login sees it read-only (plus Resync now); it is the same
              section for an admin, mounted once. */}
          <CalendarSyncSection refreshKey={refreshKey} />
          {isAdmin && <ConnectorInstructionsCard refreshKey={refreshKey} />}
        </>
      );
    case "automation":
      return (
        <>
          {isAdmin && <AutomationSection refreshKey={refreshKey} />}
          {/* Session BBN: scheduled reports, each left as a Gmail draft for the office. Nothing is sent. */}
          <ReportSchedulesSettingsCard refreshKey={refreshKey} />
        </>
      );
    case "data":
      return (
        <>
          {/* 18 October 2026: is the weekly database backup working? office and admin, read-only, derived live. */}
          <BackupStatusCard refreshKey={refreshKey} />
          {isAdmin && <BackupSection />}
          {isAdmin && <PostcodeCacheSection />}
          {isAdmin && <PrivacyNoticeSettingsCard refreshKey={refreshKey} />}
        </>
      );
    case "subcontractors":
      return (
        <>
          {/* Session AUY: the subcontractor welcome pack's CIS notice block and the V2 agreement wording confirmation. */}
          <SubcontractorDocumentsSettingsCard refreshKey={refreshKey} />
          {isAdmin && <SubcontractorXeroSettingsCard refreshKey={refreshKey} />}
          {/* Session RC1: the company standard rate card, ADMIN ONLY (rates are admin only). */}
          {isAdmin && <StandardRateCardSettingsCard refreshKey={refreshKey} />}
        </>
      );
    default:
      return null;
  }
}

// The Recent SMS log fetches for itself, so it is read only when the Email section is open (it used to load on every
// visit to the page, whichever tab was showing).
function RecentSms({ refreshKey }: { refreshKey: number }) {
  const [smsLog, setSmsLog] = useState<SmsLogItem[]>([]);
  const [smsState, setSmsState] = useState<"loading" | "ready" | "forbidden" | "error">("loading");
  // Card list below md, table otherwise -- decided once at mount (Session AGZ's mechanism, the
  // InvoicesPage precedent). jsdom has no matchMedia, so every existing test still renders the
  // table it pins.
  const [mobileCards] = useState(initialMobileLayout);

  useEffect(() => {
    let live = true;
    api.settings
      .smsLog()
      .then((rows) => {
        if (live) {
          setSmsLog(rows);
          setSmsState("ready");
        }
      })
      .catch((err) => {
        if (!live) return;
        setSmsState(err instanceof ApiError && (err.status === 403 || err.status === 401) ? "forbidden" : "error");
      });
    return () => {
      live = false;
    };
  }, [refreshKey]);

  return <RecentSmsSection smsLog={smsLog} smsState={smsState} mobileCards={mobileCards} />;
}

// The desktop-notification opt-in moved to a header icon (Session AQS, item 22):
// src/components/PushNotificationsButton.tsx, mirroring ThemeToggle's own header pattern. It
// carries its own full history and reasoning in that file's header comment.

// The Recent SMS log (Session ANQ, relocated off the main settings surface): a genuine, actively
// firing audit trail (server/src/modules/notifications/sms.ts writes a SmsLog row for every real
// engineer-invite and emergency-callout text, confirmed against that module before this section was
// touched), so it stays reachable in full -- but a live log table does not belong at full prominence
// on a configuration page, which is what the Director actually objected to. Collapsed by default,
// one full-width toggle reveals it in place, the same expand-in-place pattern the public RFID
// service page's history block already uses (src/routes/public/NfcServicePage.tsx ServiceHistory).
// Nothing here is hidden without a path back: the toggle is a single click either way.
function RecentSmsSection({
  smsLog,
  smsState,
  mobileCards,
}: {
  smsLog: SmsLogItem[];
  smsState: "loading" | "ready" | "forbidden" | "error";
  mobileCards: boolean;
}) {
  const [expanded, setExpanded] = useState(false);

  return (
    <section className="mt-5 rounded-ele border border-ele-purple-accent bg-ele-surface p-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="font-heading text-lg text-ele-blue">Recent SMS</h2>
          <p className="mt-1 text-sm text-ele-body">
            A log of every customer text sent, and any that were held back rather than sent automatically. Kept for
            reference; not shown by default.
          </p>
        </div>
        <Button variant="secondary" className="flex-none" onClick={() => setExpanded((e) => !e)} aria-expanded={expanded}>
          {expanded ? "Hide" : "Show recent SMS log"}
        </Button>
      </div>
      {expanded && (
        <div className="mt-4 border-t border-ele-purple-accent pt-4">
          {/* Session AJI: say what "withheld" means here, where the office actually meets it, rather
              than leaving them to guess from a pill. Session AKE (Director decision 17 Aug 2026)
              widened GET /api/settings/sms-log from admin-only to office-or-admin, closing the gap
              AJI flagged: the whole point of withholding rather than dropping a customer text is so
              a human can see who was not contacted, and now any office user can, not only an admin.
              The explanatory copy is still rendered only alongside the table (never above a state
              that cannot show it); "forbidden" stays reachable for a role outside office/admin (an
              engineer account browsing here directly) or a lapsed session. */}
          {smsState === "ready" && (
            <p className="mb-3 text-sm text-ele-body">
              A customer text marked withheld was not sent. The platform only texts a customer when a
              person has asked it to, so a text raised as a side effect of another action is held and
              logged here instead.
            </p>
          )}
          {smsState === "loading" ? (
            <Loader shape="inline" />
          ) : smsState === "forbidden" ? (
            <p className="text-sm text-ele-body">The SMS log is available to office staff.</p>
          ) : smsState === "error" ? (
            <p role="alert" className="text-sm text-ele-strong">Could not load the SMS log. Please try again in a moment.</p>
          ) : smsLog.length === 0 ? (
            <p className="text-sm text-ele-body">No SMS sent yet.</p>
          ) : mobileCards ? (
            // Session ALY mobile card conversion: Event and Message are hidden below md on the
            // table (no room), which drops the very content that explains a withheld row. The card
            // stacks instead, so both come back -- Event alongside the recipient and Message in its
            // own full-width block underneath, where a longer preview can wrap rather than clip.
            <MobileCardList label="Recent SMS">
              {smsLog.map((s, i) => (
                <MobileListCard
                  key={i}
                  primary={s.to}
                  secondary={s.event.replace(/_/g, " ")}
                  meta={`Sent ${formatDateTime(s.sentAt)}`}
                  right={
                    <StatusPill word={s.status} tone={s.status === "sent" ? "green" : s.status === "withheld" ? "amber" : "red"} />
                  }
                  details={s.bodyPreview}
                />
              ))}
            </MobileCardList>
          ) : (
            <div data-safetynet className="overflow-x-auto rounded-ele border border-ele-purple-accent">
              <table className="w-full text-left text-sm">
                <thead className="bg-ele-inset font-menu text-xs uppercase tracking-widest text-ele-blue">
                  <tr>
                    <th className="px-3 py-2">To</th>
                    <th className="max-md:hidden px-3 py-2">Event</th>
                    <th className="px-3 py-2">Status</th>
                    <th className="px-3 py-2">Sent</th>
                    <th className="max-md:hidden px-3 py-2">Message</th>
                  </tr>
                </thead>
                <tbody>
                  {smsLog.map((s, i) => (
                    <tr key={i} className="border-t border-ele-purple-accent">
                      <td className="px-3 py-2 align-top text-ele-strong">{s.to}</td>
                      <td className="max-md:hidden px-3 py-2 align-top">{s.event.replace(/_/g, " ")}</td>
                      <td className="px-3 py-2 align-top">
                        {/* Session AJI: three states, not two. "withheld" is a customer SMS the
                            platform deliberately did not send because no human review step stood in
                            front of its trigger. It is an advisory, not an error, so it takes the
                            amber functional-status pill rather than the red one a failure gets. */}
                        <StatusPill word={s.status} tone={s.status === "sent" ? "green" : s.status === "withheld" ? "amber" : "red"} />
                      </td>
                      <td className="px-3 py-2 align-top whitespace-nowrap">{formatDateTime(s.sentAt)}</td>
                      <td className="max-md:hidden px-3 py-2 align-top text-ele-body">{s.bodyPreview}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </section>
  );
}

// Company bank details, shown in the Company card with an inline Edit form (admin or
// office). The values flow through to payment-reminder emails. A business account, so the
// account number is shown in full. A seed default stands in until the Director saves their
// own (resolved server-side).
function BankDetailsSection({ refreshKey }: { refreshKey: number }) {
  const [bank, setBank] = useState<BankDetails | null>(null);
  const [state, setState] = useState<"loading" | "ready" | "error">("loading");
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState<BankDetails>({ bankName: "", sortCode: "", accountNumber: "" });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    api.settings
      .company()
      .then((r) => {
        if (!live) return;
        setBank(r.bank);
        setState("ready");
      })
      .catch(() => live && setState("error"));
    return () => {
      live = false;
    };
  }, [refreshKey]);

  function startEdit() {
    if (bank) setForm(bank);
    setError(null);
    setEditing(true);
  }

  async function save() {
    if (!form.bankName.trim() || !form.sortCode.trim() || !form.accountNumber.trim()) {
      setError("All three bank fields are required.");
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const r = await api.settings.updateBank({
        bankName: form.bankName.trim(),
        sortCode: form.sortCode.trim(),
        accountNumber: form.accountNumber.trim(),
      });
      setBank(r.bank);
      setEditing(false);
    } catch (e) {
      setError(e instanceof ApiError ? e.message || e.code : "Could not save the bank details. Please try again.");
    } finally {
      setSaving(false);
    }
  }

  const inputCls = "rounded-ele border border-ele-purple-accent bg-ele-field px-3 py-2 text-sm text-ele-strong outline-none focus:border-ele-green";
  const labelCls = "mb-1 block font-menu text-xs uppercase tracking-wide text-ele-body";

  return (
    <div className="mt-4 border-t border-ele-purple-accent pt-4">
      <div className="mb-2 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <h3 className="font-heading text-sm uppercase tracking-wide text-ele-body">Bank details</h3>
        {state === "ready" && !editing && (
          <div className="flex flex-wrap items-center gap-2">
            <button type="button" onClick={startEdit} className="max-sm:inline-flex max-sm:min-h-11 max-sm:items-center font-menu text-xs tracking-wide text-ele-green hover:underline">
              Edit
            </button>
          </div>
        )}
      </div>
      {state === "loading" ? (
        <Loader shape="inline" />
      ) : state === "error" ? (
        <p role="alert" className="text-sm text-ele-strong">Could not load the bank details.</p>
      ) : editing ? (
        <div className="space-y-3">
          <label className="block">
            <span className={labelCls}>Bank</span>
            <input aria-label="Bank name" value={form.bankName} onChange={(e) => setForm((f) => ({ ...f, bankName: e.target.value }))} className={`${inputCls} w-full`} />
          </label>
          <label className="block">
            <span className={labelCls}>Sort code</span>
            <input aria-label="Sort code" value={form.sortCode} onChange={(e) => setForm((f) => ({ ...f, sortCode: e.target.value }))} className={`${inputCls} w-full`} />
          </label>
          <label className="block">
            <span className={labelCls}>Account number</span>
            <input aria-label="Account number" value={form.accountNumber} onChange={(e) => setForm((f) => ({ ...f, accountNumber: e.target.value }))} className={`${inputCls} w-full`} />
          </label>
          {error && <p role="alert" className="text-sm text-ele-strong">{error}</p>}
          <div className="flex gap-2">
            <Button size="sm" onClick={save} disabled={saving}>
              {saving ? "Saving..." : "Save"}
            </Button>
            <Button variant="ghost" size="sm" onClick={() => setEditing(false)} disabled={saving}>
              Cancel
            </Button>
          </div>
        </div>
      ) : bank ? (
        <dl className="space-y-1 text-sm">
          <div className="flex justify-between gap-3"><dt className="text-ele-body">Bank</dt><dd className="text-ele-strong">{bank.bankName}</dd></div>
          <div className="flex justify-between gap-3"><dt className="text-ele-body">Sort code</dt><dd className="text-ele-strong">{bank.sortCode}</dd></div>
          <div className="flex justify-between gap-3"><dt className="text-ele-body">Account number</dt><dd className="text-ele-strong">{bank.accountNumber}</dd></div>
        </dl>
      ) : null}
    </div>
  );
}

// Editable CPI rate (used for renewal pricing) with the locked escalator shown for
// reference. The escalator rate/cap/floor are locked operational values; only the
// current ONS CPI is editable here, and confirming it makes renewal figures firm.
function CompanySettings({ refreshKey }: { refreshKey: number }) {
  const [settings, setSettings] = useState<TenantSettings | null>(null);
  const [cpiPercent, setCpiPercent] = useState("");
  const [state, setState] = useState<"loading" | "ready" | "error">("loading");
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  // Session AJG: saving and clearing are different acts with different consequences, so the
  // confirmation says which happened rather than one generic "Saved".
  const [savedMessage, setSavedMessage] = useState("");
  const [error, setError] = useState<string | null>(null);
  // PPM payment-overdue runway (Session AAT Loop 1): a whole number of days, editable here.
  const [runwayDays, setRunwayDays] = useState("");
  const [runwaySaving, setRunwaySaving] = useState(false);
  const [runwaySaved, setRunwaySaved] = useState(false);
  const [runwayError, setRunwayError] = useState<string | null>(null);
  // Quote validity window (Session AAT Loop 2): a whole number of days, editable here.
  const [validityDays, setValidityDays] = useState("");
  const [validitySaving, setValiditySaving] = useState(false);
  const [validitySaved, setValiditySaved] = useState(false);
  const [validityError, setValidityError] = useState<string | null>(null);
  // Labour cost per hour (Session AAT Loop 5): the office-only margin basis, editable here.
  const [labourCost, setLabourCost] = useState("");
  const [labourSaving, setLabourSaving] = useState(false);
  const [labourSaved, setLabourSaved] = useState(false);
  const [labourError, setLabourError] = useState<string | null>(null);
  // Call-log NPS send delay (Session ABT): minutes between logging an inbound call with "Send NPS"
  // on and the survey sending, so the caller has time to get off the phone.
  const [callNpsDelay, setCallNpsDelay] = useState("");
  const [callNpsSaving, setCallNpsSaving] = useState(false);
  const [callNpsSaved, setCallNpsSaved] = useState(false);
  const [callNpsError, setCallNpsError] = useState<string | null>(null);
  // Inbox-triage chase threshold (Session AMR): working days an awaiting-reply email may sit
  // before it escalates to needs_chase on the Inbox triage page and the morning briefing.
  const [chaseThreshold, setChaseThreshold] = useState("");
  const [chaseThresholdSaving, setChaseThresholdSaving] = useState(false);
  const [chaseThresholdSaved, setChaseThresholdSaved] = useState(false);
  const [chaseThresholdError, setChaseThresholdError] = useState<string | null>(null);
  // Knowledge Base trusted senders (Session AGJ): the addresses whose PDF attachments the hourly
  // Gmail sweep ingests by itself. Held as a DRAFT array so the office can add and remove several
  // before committing, then saved as one whole list (the PATCH replaces, it does not merge).
  const [senders, setSenders] = useState<string[]>([]);
  const [sendersCustomised, setSendersCustomised] = useState(false);
  const [senderDraft, setSenderDraft] = useState("");
  const [sendersDirty, setSendersDirty] = useState(false);
  const [sendersSaving, setSendersSaving] = useState(false);
  const [sendersSaved, setSendersSaved] = useState<string | null>(null);
  const [sendersError, setSendersError] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    api.settings
      .tenant()
      .then((s) => {
        if (!live) return;
        setSettings(s);
        setCpiPercent(s.cpiRate != null ? String(Number((s.cpiRate * 100).toFixed(2))) : "");
        setRunwayDays(String(s.ppmRunwayDays));
        setValidityDays(String(s.quoteValidityDays));
        setLabourCost(String(s.labourCostPerHour));
        setCallNpsDelay(String(s.callNpsDelayMinutes));
        setChaseThreshold(String(s.inboxChaseThresholdDays));
        setSenders(s.kbTrustedSenders);
        setSendersCustomised(s.kbTrustedSendersCustomised);
        setSendersDirty(false);
        setState("ready");
      })
      .catch(() => live && setState("error"));
    return () => {
      live = false;
    };
  }, [refreshKey]);

  async function save() {
    setError(null);
    setSaved(false);
    const percent = Number(cpiPercent);
    // An empty box is still refused rather than treated as a clear, deliberately: clearing is a
    // distinct, consequential act (it changes what every future renewal is priced from) and it has
    // its own control below. Silently reading a blank field as "clear" would make an accidental
    // deletion indistinguishable from a deliberate one.
    if (cpiPercent.trim() === "" || Number.isNaN(percent) || percent < 0 || percent > 50) {
      setError("Enter the CPI as a percentage between 0 and 50, e.g. 3.2. To go back to the automatic ONS figure, use Clear override.");
      return;
    }
    setSaving(true);
    try {
      const updated = await api.settings.updateTenant({ cpiRate: Number((percent / 100).toFixed(4)) });
      setSettings(updated);
      setSavedMessage("Saved. Renewals are now priced from the figure entered here, not the live ONS reading.");
      setSaved(true);
    } catch {
      setError("Could not save the CPI rate. Please try again in a moment.");
    } finally {
      setSaving(false);
    }
  }

  // Session AJG: clear the manual override so the live ONS reading is used again. Sends an explicit
  // null -- NEVER 0, which the resolver reads as a real "zero inflation" override and which would
  // price every renewal at the operational uplift alone while looking cleared on screen.
  async function clearCpi() {
    setError(null);
    setSaved(false);
    setSaving(true);
    try {
      const updated = await api.settings.updateTenant({ cpiRate: null });
      setSettings(updated);
      setCpiPercent("");
      // Say plainly when clearing leaves NO figure at all. The live reading can legitimately be
      // absent (a fresh tenant, or an ONS integration that has never succeeded), and in that state
      // renewals cannot be priced at all until the daily cron next succeeds -- which is a very
      // different outcome from "back to normal" and must not be reported as the same thing.
      //
      // Keyed on the SERVER's post-clear verdict, not on cpiFetchedRate: a cached rate with no
      // fetchedAt is not a usable reading, and claiming otherwise would contradict the price the
      // platform will actually produce.
      setSavedMessage(
        updated.cpiResolved.rate != null
          ? `Cleared. Renewals are priced from the live ONS reading again (${pct(updated.cpiResolved.rate)}).`
          : "Cleared. There is no usable live ONS reading yet, so renewal prices cannot be calculated until the next automatic refresh succeeds.",
      );
      setSaved(true);
    } catch {
      setError("Could not clear the CPI override. Please try again in a moment.");
    } finally {
      setSaving(false);
    }
  }

  async function saveRunway() {
    setRunwayError(null);
    setRunwaySaved(false);
    const days = Number(runwayDays);
    if (runwayDays.trim() === "" || !Number.isInteger(days) || days < 1 || days > 365) {
      setRunwayError("Enter the runway as a whole number of days between 1 and 365.");
      return;
    }
    setRunwaySaving(true);
    try {
      const updated = await api.settings.updateTenant({ ppmRunwayDays: days });
      setSettings(updated);
      setRunwayDays(String(updated.ppmRunwayDays));
      setRunwaySaved(true);
    } catch {
      setRunwayError("Could not save the payment runway. Please try again in a moment.");
    } finally {
      setRunwaySaving(false);
    }
  }

  async function saveValidity() {
    setValidityError(null);
    setValiditySaved(false);
    const days = Number(validityDays);
    if (validityDays.trim() === "" || !Number.isInteger(days) || days < 1 || days > 365) {
      setValidityError("Enter the validity as a whole number of days between 1 and 365.");
      return;
    }
    setValiditySaving(true);
    try {
      const updated = await api.settings.updateTenant({ quoteValidityDays: days });
      setSettings(updated);
      setValidityDays(String(updated.quoteValidityDays));
      setValiditySaved(true);
    } catch {
      setValidityError("Could not save the quote validity. Please try again in a moment.");
    } finally {
      setValiditySaving(false);
    }
  }

  async function saveLabour() {
    setLabourError(null);
    setLabourSaved(false);
    const cost = Number(labourCost);
    if (labourCost.trim() === "" || Number.isNaN(cost) || cost <= 0 || cost > 1000) {
      setLabourError("Enter the labour cost per hour as a number between 1 and 1000.");
      return;
    }
    setLabourSaving(true);
    try {
      const updated = await api.settings.updateTenant({ labourCostPerHour: cost });
      setSettings(updated);
      setLabourCost(String(updated.labourCostPerHour));
      setLabourSaved(true);
    } catch {
      setLabourError("Could not save the labour cost. Please try again in a moment.");
    } finally {
      setLabourSaving(false);
    }
  }

  async function saveCallNpsDelay() {
    setCallNpsError(null);
    setCallNpsSaved(false);
    const mins = Number(callNpsDelay);
    if (callNpsDelay.trim() === "" || !Number.isInteger(mins) || mins < 0 || mins > 1440) {
      setCallNpsError("Enter the delay as a whole number of minutes between 0 and 1440.");
      return;
    }
    setCallNpsSaving(true);
    try {
      const updated = await api.settings.updateTenant({ callNpsDelayMinutes: mins });
      setSettings(updated);
      setCallNpsDelay(String(updated.callNpsDelayMinutes));
      setCallNpsSaved(true);
    } catch {
      setCallNpsError("Could not save the survey delay. Please try again in a moment.");
    } finally {
      setCallNpsSaving(false);
    }
  }

  async function saveChaseThreshold() {
    setChaseThresholdError(null);
    setChaseThresholdSaved(false);
    const days = Number(chaseThreshold);
    if (chaseThreshold.trim() === "" || !Number.isInteger(days) || days < 1 || days > 60) {
      setChaseThresholdError("Enter the chase threshold as a whole number of days between 1 and 60.");
      return;
    }
    setChaseThresholdSaving(true);
    try {
      const updated = await api.settings.updateTenant({ inboxChaseThresholdDays: days });
      setSettings(updated);
      setChaseThreshold(String(updated.inboxChaseThresholdDays));
      setChaseThresholdSaved(true);
    } catch {
      setChaseThresholdError("Could not save the chase threshold. Please try again in a moment.");
    } finally {
      setChaseThresholdSaving(false);
    }
  }

  function addSender() {
    setSendersError(null);
    setSendersSaved(null);
    const next = senderDraft.trim().toLowerCase();
    if (next === "") {
      setSendersError("Enter an email address to add.");
      return;
    }
    if (!TRUSTED_SENDER_PATTERN.test(next)) {
      setSendersError("Enter a full email address, for example enquiries@elti-uk.com");
      return;
    }
    if (senders.includes(next)) {
      setSendersError("That address is already on the list.");
      return;
    }
    if (senders.length >= KB_TRUSTED_SENDERS_MAX) {
      setSendersError(`You cannot have more than ${KB_TRUSTED_SENDERS_MAX} trusted senders.`);
      return;
    }
    setSenders([...senders, next]);
    setSenderDraft("");
    setSendersDirty(true);
  }

  function removeSender(address: string) {
    setSendersError(null);
    setSendersSaved(null);
    setSenders(senders.filter((s) => s !== address));
    setSendersDirty(true);
  }

  // Saves the WHOLE list. An empty list is a deliberate reset: the sweep reads it as "use the
  // built-in defaults", so the confirmation says so rather than letting the office believe it has
  // switched the sweep off.
  async function saveSenders(list: string[]) {
    setSendersError(null);
    setSendersSaved(null);
    setSendersSaving(true);
    try {
      const updated = await api.settings.updateTenant({ kbTrustedSenders: list });
      setSettings(updated);
      // Adopt the SERVER's resolved list, never the local draft: it has been normalised and, if the
      // list was emptied, it now holds the defaults. Showing the draft would show a list the sweep
      // is not using.
      setSenders(updated.kbTrustedSenders);
      setSendersCustomised(updated.kbTrustedSendersCustomised);
      setSendersDirty(false);
      setSendersSaved(
        updated.kbTrustedSendersCustomised
          ? "Saved. The hourly sweep now reads mail from these addresses."
          : "Saved. The list is back to the built-in default senders.",
      );
    } catch {
      setSendersError("Could not save the trusted senders. Please try again in a moment.");
    } finally {
      setSendersSaving(false);
    }
  }

  return (
    <section className="mt-5 rounded-ele border border-ele-purple-accent bg-ele-surface p-5">
      <h2 className="mb-3 font-heading text-lg text-ele-blue">Company settings</h2>
      {state === "loading" ? (
        <Loader shape="inline" />
      ) : state === "error" ? (
        <p role="alert" className="text-sm text-ele-strong">Could not load company settings.</p>
      ) : (
        <>
          {/* Session AJG: this copy used to read "Confirm this cycle's CPI from the ONS figure so
              renewal letters quote a firm price" -- written for the pre-D13 model where an
              unconfirmed CPI priced renewals at zero. Since Session AIC the platform reads CPI live
              from the ONS itself, and typing a figure here creates a permanent OVERRIDE of it.
              Session AIC deleted that instruction from the Renewals page and it was left standing
              here, so the page went on teaching the one action that pins every future renewal to a
              hand-typed number. It now says what the field actually is. */}
          <p className="mb-3 text-sm text-ele-body">
            Renewal pricing uses CPI plus the locked operational uplift. CPI is read automatically from the ONS every
            day, so normally there is nothing to enter here. Only type a figure if the automatic reading is unavailable
            and a renewal has to go out: it overrides the live figure for every renewal until you clear it.
          </p>
          <dl className="mb-3 space-y-1 rounded-ele border border-ele-purple-accent bg-ele-field px-3 py-2 text-sm">
            <div className="flex justify-between gap-3">
              <dt className="text-ele-body">Live ONS reading</dt>
              <dd className="text-ele-strong">
                {settings?.cpiFetchedRate != null
                  ? `${pct(settings.cpiFetchedRate)}${settings.cpiFetchedPeriod ? ` (${settings.cpiFetchedPeriod})` : ""}`
                  : "Not available"}
              </dd>
            </div>
            {/* Read from the SERVER's resolved verdict, never re-derived here. resolveRenewalCpi
                requires both a fetched rate and a fetchedAt before it will use the live reading, so
                a local check on cpiFetchedRate alone would claim renewals are priced from ONS in a
                state where nothing can be priced at all. */}
            <div className="flex justify-between gap-3">
              <dt className="text-ele-body">Used for renewals now</dt>
              <dd className="text-ele-strong">
                {settings?.cpiResolved.rate == null
                  ? "No figure available"
                  : `${pct(settings.cpiResolved.rate)} (${settings.cpiResolved.source === "manual" ? "entered here" : "live ONS"})`}
              </dd>
            </div>
          </dl>
          {settings?.cpiFetchError && (
            <p role="alert" className="mb-3 text-sm text-ele-danger">
              The automatic ONS refresh is failing: {settings.cpiFetchError}
            </p>
          )}
          {/* The override state is called out explicitly, because before this a figure typed once
              was invisible for ever: the resolver returned no message in the ordinary case and the
              Renewals stale banner is structurally unreachable while an override is set. */}
          {settings?.cpiResolved.source === "manual" && (
            <p role="status" className="mb-3 text-sm text-amber-400">
              A CPI figure entered here is currently overriding the automatic reading.
              {settings.cpiResolved.overridingRate != null
                ? ` Renewals are priced at ${pct(settings.cpiResolved.rate)} rather than the live ${pct(settings.cpiResolved.overridingRate)}.`
                : ""}
            </p>
          )}
          <label className="mb-1 block text-sm text-ele-body" htmlFor="cpi">Manual CPI override (%)</label>
          <div className="flex flex-wrap gap-2">
            <input
              id="cpi"
              type="number"
              step="0.1"
              min="0"
              max="50"
              value={cpiPercent}
              onChange={(e) => setCpiPercent(e.target.value)}
              placeholder="e.g. 3.2"
              className="w-32 rounded-ele border border-ele-purple-accent bg-ele-field px-3 py-2 outline-none focus:border-ele-green"
            />
            <Button size="sm" onClick={save} disabled={saving}>
              {saving ? "Saving..." : "Save"}
            </Button>
            {/* THE CONTROL THAT DID NOT EXIST. Sends an explicit null: the ONLY way to clear the
                override. Emptying the box and pressing Save is deliberately NOT a clear (it is
                refused as invalid), and typing 0 is a real override meaning zero inflation, not a
                clear -- so a dedicated control is the only unambiguous way to express it.
                TWO-STEP, per the Session AEH branded-confirm rule: clearing changes what every
                future renewal is priced from, which is consequential even though nothing is
                deleted, and a single stray click should not be able to do it. */}
            {settings?.cpiResolved.source === "manual" && (
              <ConfirmButton
                onConfirm={clearCpi}
                label="Clear override, use the live ONS figure"
                confirmLabel="Clear it? Renewals will reprice from the live figure"
                busy={saving}
                busyLabel="Clearing..."
                className="rounded-ele border border-ele-purple-accent px-4 py-2 font-menu text-xs tracking-wide text-ele-body transition hover:border-ele-green hover:text-ele-strong"
              />
            )}
          </div>
          {error && <p role="alert" className="mt-2 text-sm text-ele-danger">{error}</p>}
          {saved && <p className="mt-2 text-sm text-ele-green">{savedMessage}</p>}

          <dl className="mt-4 space-y-1 border-t border-ele-purple-accent pt-3 text-sm">
            <div className="flex justify-between gap-3"><dt className="text-ele-body">Operational uplift (locked)</dt><dd className="text-ele-strong">{pct(settings?.escalatorRate ?? null)}</dd></div>
            <div className="flex justify-between gap-3"><dt className="text-ele-body">Yearly cap (locked)</dt><dd className="text-ele-strong">{pct(settings?.escalatorCap ?? null)}</dd></div>
            <div className="flex justify-between gap-3"><dt className="text-ele-body">Yearly floor (locked)</dt><dd className="text-ele-strong">{pct(settings?.escalatorFloor ?? null)}</dd></div>
          </dl>

          <div className="mt-4 border-t border-ele-purple-accent pt-3">
            <p className="mb-2 text-sm text-ele-body">
              PPM payment runway: how many days before a service date an unpaid visit is flagged Payment overdue on the
              PPM payments page. Visits are never held; this only sets when the red flag appears.
            </p>
            <label className="mb-1 block text-sm text-ele-body" htmlFor="runway">Payment runway (days)</label>
            <div className="flex gap-2">
              <input
                id="runway"
                type="number"
                step="1"
                min="1"
                max="365"
                value={runwayDays}
                onChange={(e) => setRunwayDays(e.target.value)}
                placeholder="e.g. 14"
                className="w-32 rounded-ele border border-ele-purple-accent bg-ele-field px-3 py-2 outline-none focus:border-ele-green"
              />
              <Button size="sm" onClick={saveRunway} disabled={runwaySaving}>
                {runwaySaving ? "Saving..." : "Save"}
              </Button>
            </div>
            {runwayError && <p role="alert" className="mt-2 text-sm text-ele-danger">{runwayError}</p>}
            {runwaySaved && <p className="mt-2 text-sm text-ele-green">Saved. The PPM payments page now uses this runway.</p>}
          </div>

          <div className="mt-4 border-t border-ele-purple-accent pt-3">
            <p className="mb-2 text-sm text-ele-body">
              Quote validity: how many days a sent quote stays acceptable. After this the quote expires and the customer
              cannot accept it online (you can Extend it or Re-quote from the Quotes page).
            </p>
            <label className="mb-1 block text-sm text-ele-body" htmlFor="validity">Quote validity (days)</label>
            <div className="flex gap-2">
              <input
                id="validity"
                type="number"
                step="1"
                min="1"
                max="365"
                value={validityDays}
                onChange={(e) => setValidityDays(e.target.value)}
                placeholder="e.g. 30"
                className="w-32 rounded-ele border border-ele-purple-accent bg-ele-field px-3 py-2 outline-none focus:border-ele-green"
              />
              <Button size="sm" onClick={saveValidity} disabled={validitySaving}>
                {validitySaving ? "Saving..." : "Save"}
              </Button>
            </div>
            {validityError && <p role="alert" className="mt-2 text-sm text-ele-danger">{validityError}</p>}
            {validitySaved && <p className="mt-2 text-sm text-ele-green">Saved. New quotes now use this validity window.</p>}
          </div>

          <div className="mt-4 border-t border-ele-purple-accent pt-3">
            <p className="mb-2 text-sm text-ele-body">
              Labour cost per hour: the loaded engineer cost used for the office-only margin figures shown on the quote
              calculator. This is never shown to a customer and never changes a quoted price. Set your true loaded cost.
            </p>
            <label className="mb-1 block text-sm text-ele-body" htmlFor="labour">Labour cost (£ per hour)</label>
            <div className="flex gap-2">
              <input
                id="labour"
                type="number"
                step="0.5"
                min="1"
                max="1000"
                value={labourCost}
                onChange={(e) => setLabourCost(e.target.value)}
                placeholder="e.g. 62"
                className="w-32 rounded-ele border border-ele-purple-accent bg-ele-field px-3 py-2 outline-none focus:border-ele-green"
              />
              <Button size="sm" onClick={saveLabour} disabled={labourSaving}>
                {labourSaving ? "Saving..." : "Save"}
              </Button>
            </div>
            {labourError && <p role="alert" className="mt-2 text-sm text-ele-danger">{labourError}</p>}
            {labourSaved && <p className="mt-2 text-sm text-ele-green">Saved. The calculator margin figures now use this cost.</p>}
          </div>

          <div className="mt-4 border-t border-ele-purple-accent pt-3">
            <p className="mb-2 text-sm text-ele-body">
              {/* Session AHY: the survey is drafted, not sent. The delay still governs when the draft
                  appears, so the caller has hung up before anyone can send it, but the send is a
                  person's now. This page configures the same number LogCallModal describes, so the
                  two must not contradict each other. */}
              Call survey delay: when the office logs an inbound call with the feedback survey turned on, the survey is
              drafted this many minutes later, so the caller has time to get off the phone before you review and send it.
              Set 0 to draft it straight away.
            </p>
            <label className="mb-1 block text-sm text-ele-body" htmlFor="call-nps-delay">Survey delay (minutes)</label>
            <div className="flex gap-2">
              <input
                id="call-nps-delay"
                type="number"
                step="1"
                min="0"
                max="1440"
                value={callNpsDelay}
                onChange={(e) => setCallNpsDelay(e.target.value)}
                placeholder="e.g. 60"
                className="w-32 rounded-ele border border-ele-purple-accent bg-ele-field px-3 py-2 outline-none focus:border-ele-green"
              />
              <Button size="sm" onClick={saveCallNpsDelay} disabled={callNpsSaving}>
                {callNpsSaving ? "Saving..." : "Save"}
              </Button>
            </div>
            {callNpsError && <p role="alert" className="mt-2 text-sm text-ele-danger">{callNpsError}</p>}
            {callNpsSaved && <p className="mt-2 text-sm text-ele-green">Saved. Call feedback surveys now use this delay.</p>}
          </div>

          <div className="mt-4 border-t border-ele-purple-accent pt-3">
            <p className="mb-2 text-sm text-ele-body">
              Inbox chase threshold: how many working days an email waiting on a reply from ELE may sit before the Inbox
              triage page and the morning briefing escalate it to Needs chase. A quote chase and a routine reminder do
              not warrant the same patience, so set this to whatever fits how ELE actually works.
            </p>
            <label className="mb-1 block text-sm text-ele-body" htmlFor="chase-threshold">Chase threshold (working days)</label>
            <div className="flex gap-2">
              <input
                id="chase-threshold"
                type="number"
                step="1"
                min="1"
                max="60"
                value={chaseThreshold}
                onChange={(e) => setChaseThreshold(e.target.value)}
                placeholder="e.g. 5"
                className="w-32 rounded-ele border border-ele-purple-accent bg-ele-field px-3 py-2 outline-none focus:border-ele-green"
              />
              <Button size="sm" onClick={saveChaseThreshold} disabled={chaseThresholdSaving}>
                {chaseThresholdSaving ? "Saving..." : "Save"}
              </Button>
            </div>
            {chaseThresholdError && <p role="alert" className="mt-2 text-sm text-ele-danger">{chaseThresholdError}</p>}
            {chaseThresholdSaved && <p className="mt-2 text-sm text-ele-green">Saved. The Inbox triage page and the morning briefing now use this threshold.</p>}
          </div>

          <div className="mt-4 border-t border-ele-purple-accent pt-3">
            <p className="mb-2 text-sm text-ele-body">
              Knowledge base senders: when one of these addresses emails a PDF to the office inbox, it is added to the
              knowledge base by itself within the hour, so an engineer can search it. Anything else can still be added
              by hand, or by applying the ELE-KB-INGEST label to the email.
            </p>
            {/* Two honesty points the office cannot work out from the list alone, and both matter.
                (1) This list does NOT widen whose reports get read into a proposed LOLER record: that
                    is a separate allow-list on purpose, so adding a parts supplier here can never put
                    a supplier's PDF in front of the office as a statutory examination.
                (2) Removing everything RESTORES the defaults, it does not stop the sweep. The sweep's
                    resolver reads an empty list as "use the seed", so an office told otherwise would
                    think it had switched ingestion off while ELTI's mail kept arriving. */}
            <p className="mb-2 text-xs text-ele-body">
              This only affects the knowledge base. It does not change whose LOLER reports the platform offers to file
              as inspection records.
            </p>
            {senders.length === 0 ? (
              // Names the consequence BEFORE the office presses Save, in the one state where the
              // screen would otherwise mislead: an empty list is a reset, not an off switch, and
              // "No senders on the list." on its own reads as the latter.
              <p className="mb-2 text-sm text-ele-body">
                No senders on the list. Saving now puts the built-in default senders back: it does not switch off
                the hourly check.
              </p>
            ) : (
              <ul className="mb-2 space-y-1">
                {senders.map((s) => (
                  <li key={s} className="flex items-center justify-between gap-2 rounded-ele bg-ele-field px-3 py-2">
                    <span className="truncate text-sm text-ele-strong">{s}</span>
                    <button
                      type="button"
                      onClick={() => removeSender(s)}
                      disabled={sendersSaving}
                      aria-label={`Remove ${s}`}
                      className="flex-none rounded-ele px-2 py-1 font-menu text-xs tracking-wide text-ele-danger transition hover:brightness-110 disabled:opacity-50"
                    >
                      Remove
                    </button>
                  </li>
                ))}
              </ul>
            )}
            {!sendersCustomised && !sendersDirty && (
              <p className="mb-2 text-xs text-ele-body">These are the built-in default senders. Nothing has been set here yet.</p>
            )}
            <label className="mb-1 block text-sm text-ele-body" htmlFor="kb-sender">Add a sender</label>
            <div className="flex flex-wrap gap-2">
              <input
                id="kb-sender"
                type="email"
                value={senderDraft}
                onChange={(e) => setSenderDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    addSender();
                  }
                }}
                placeholder="e.g. parts@supplier.co.uk"
                className="min-w-0 flex-1 rounded-ele border border-ele-purple-accent bg-ele-field px-3 py-2 outline-none focus:border-ele-green"
              />
              <button
                type="button"
                onClick={addSender}
                disabled={sendersSaving}
                className="rounded-ele border border-ele-purple-accent px-4 py-2 font-menu text-xs tracking-wide text-ele-body transition hover:brightness-110 disabled:opacity-50"
              >
                Add
              </button>
              <Button size="sm" onClick={() => saveSenders(senders)} disabled={sendersSaving || !sendersDirty}>
                {sendersSaving ? "Saving..." : "Save senders"}
              </Button>
            </div>
            {/* Hidden once the draft is already empty: there, "Save senders" and "Restore the default
                senders" would sit side by side doing exactly the same thing, which invites the reader
                to think one of them means something else. */}
            {(sendersCustomised || sendersDirty) && senders.length > 0 && (
              <button
                type="button"
                onClick={() => saveSenders([])}
                disabled={sendersSaving}
                className="mt-2 font-menu text-xs tracking-wide text-ele-body underline transition hover:brightness-110 disabled:opacity-50"
              >
                Restore the default senders
              </button>
            )}
            {/* Shown whenever the draft differs from what the server holds, INCLUDING after a failed
                save. Suppressing it on an error was the first cut and it was wrong: the list still
                displays the added address, so without this line a failed save leaves the office
                looking at an address that is not in force and nothing saying so. */}
            {sendersDirty && (
              <p className="mt-2 text-sm text-ele-body">Not saved yet. Press Save senders to apply the change.</p>
            )}
            {sendersError && <p role="alert" className="mt-2 text-sm text-ele-danger">{sendersError}</p>}
            {sendersSaved && <p className="mt-2 text-sm text-ele-green">{sendersSaved}</p>}
          </div>
        </>
      )}
    </section>
  );
}

// A single on/off switch row (brand-green when on). Pure presentational.
function ToggleRow({ title, description, on, label, disabled, onToggle }: { title: string; description: string; on: boolean; label: string; disabled: boolean; onToggle: () => void }) {
  return (
    <div className="flex items-start justify-between gap-4">
      <div>
        <p className="text-sm text-ele-strong">{title}</p>
        <p className="mt-1 text-sm text-ele-body">{description}</p>
      </div>
      <button
        type="button"
        role="switch"
        aria-checked={on}
        aria-label={label}
        disabled={disabled}
        onClick={onToggle}
        className={`relative mt-1 h-6 w-11 shrink-0 rounded-full transition disabled:opacity-50 ${on ? "bg-ele-green" : "bg-[var(--ele-switch-track)]"}`}
      >
        <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white transition-all ${on ? "left-[22px]" : "left-0.5"}`} />
      </button>
    </div>
  );
}

// Automation (admin only): two toggles, both default on.
//  - Automatic PPM job creation: the daily cron creates the forward PPM service visits from
//    every active contract (the on-demand "Generate PPM" button is unaffected either way).
//  - Automatic draft invoicing: completed call-out / repair jobs and logged call-outs raise a
//    Xero DRAFT invoice automatically. When off, V2 raises no drafts and you invoice by hand.
//    Either way invoices are only ever DRAFT, never sent automatically.
function AutomationSection({ refreshKey }: { refreshKey: number }) {
  const [settings, setSettings] = useState<AutomationSettings | null>(null);
  const [state, setState] = useState<"loading" | "ready" | "error">("loading");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    api.settings
      .automation()
      .then((s) => {
        if (!live) return;
        setSettings(s);
        setState("ready");
      })
      .catch(() => live && setState("error"));
    return () => {
      live = false;
    };
  }, [refreshKey]);

  async function toggle(patch: { ppmAutoGenerate?: boolean; autoInvoiceEnabled?: boolean }) {
    setSaving(true);
    setError(null);
    const previous = settings;
    if (settings) setSettings({ ...settings, ...patch }); // optimistic
    try {
      setSettings(await api.settings.updateAutomation(patch));
    } catch {
      setSettings(previous); // revert on failure
      setError("Could not save the automation setting. Please try again in a moment.");
    } finally {
      setSaving(false);
    }
  }

  const ppmOn = settings?.ppmAutoGenerate ?? true;
  const billingOn = settings?.autoInvoiceEnabled ?? true;

  return (
    <section className="mt-5 rounded-ele border border-ele-purple-accent bg-ele-surface p-5">
      <h2 className="mb-3 font-heading text-lg text-ele-blue">Automation</h2>
      {state === "loading" ? (
        <Loader shape="inline" />
      ) : state === "error" ? (
        <p role="alert" className="text-sm text-ele-strong">Could not load the automation settings.</p>
      ) : (
        <div className="space-y-5">
          <ToggleRow
            title="Automatic PPM job creation"
            description="When on, the daily schedule creates the next planned maintenance visits from each active contract. When off, you create maintenance visits on demand from the Scheduling page."
            on={ppmOn}
            label="Automatic PPM job creation"
            disabled={saving}
            onToggle={() => toggle({ ppmAutoGenerate: !ppmOn })}
          />
          <div className="border-t border-ele-purple-accent" />
          <ToggleRow
            title="Automatic draft invoicing"
            description="When on, a completed call-out or repair job raises a draft invoice in Xero automatically for you to review and send. When off, no drafts are created and you raise invoices by hand. Either way, invoices are always created as drafts and never sent to the customer automatically."
            on={billingOn}
            label="Automatic draft invoicing"
            disabled={saving}
            onToggle={() => toggle({ autoInvoiceEnabled: !billingOn })}
          />
        </div>
      )}
      {error && <p role="alert" className="mt-2 text-sm text-ele-danger">{error}</p>}
    </section>
  );
}

// Database backup (admin only). A weekly automated backup is filed to Google Drive;
// this is the on-demand manual download. The download streams a fresh gzipped dump
// of the whole database straight to the browser; nothing is stored on the server.
function BackupSection() {
  return (
    <section className="mt-5 rounded-ele border border-ele-purple-accent bg-ele-surface p-5">
      <h2 className="mb-3 font-heading text-lg text-ele-blue">Database backup</h2>
      <p className="mb-3 text-sm text-ele-body">
        A full backup is filed to Google Drive automatically every week. You can also download a fresh backup now: it
        is a complete, compressed copy of the database. Keep it somewhere safe.
      </p>
      <Button onClick={() => window.location.assign(api.admin.backupDownloadHref())}>
        Download Database Backup
      </Button>
    </section>
  );
}

// Postcode lookup cache (admin only, Session AAP). The office quote calculator caches free
// postcodes.io lookups (town/district/region + coordinates) so a repeat postcode resolves instantly.
// This clears the cache: a postcode is then re-fetched fresh on its next lookup. Cheap and safe (a
// pure performance cache; pricing never reads it), provided per the DELETE RULE.
function PostcodeCacheSection() {
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  async function clearCache() {
    setBusy(true);
    setMsg(null);
    setErr(null);
    try {
      const res = await api.address.clearCache();
      setMsg(`Cleared ${res.deleted} cached postcode${res.deleted === 1 ? "" : "s"}.`);
    } catch (e) {
      setErr(friendlyApiError(e, "Could not clear the postcode cache. Please try again in a moment."));
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="mt-5 rounded-ele border border-ele-purple-accent bg-ele-surface p-5">
      <h2 className="mb-3 font-heading text-lg text-ele-blue">Postcode lookup cache</h2>
      <p className="mb-3 text-sm text-ele-body">
        The quote calculator remembers each postcode it looks up (town, district and coordinates) so it resolves
        instantly next time. Clearing the cache makes every postcode fetch fresh on its next lookup. Pricing is never
        affected.
      </p>
      <ConfirmButton
        onConfirm={() => void clearCache()}
        label="Clear postcode cache"
        confirmLabel="Confirm clear?"
        busy={busy}
        busyLabel="Clearing..."
        className="rounded-ele border border-ele-danger px-4 py-2 font-menu text-xs tracking-wide text-ele-danger transition hover:bg-ele-danger-fill hover:text-white disabled:opacity-50"
        armedClassName="bg-ele-danger-fill !text-white"
      />
      {msg && <p role="status" className="mt-2 text-sm text-ele-green">{msg}</p>}
      {err && <p role="alert" className="mt-2 text-sm text-ele-danger">{err}</p>}
    </section>
  );
}

// An integration tile's action: an OAuth (re)connect that navigates the CURRENT tab
// (the provider redirects back), or an external console/account link opened in a NEW
// tab. Null when the integration is host-configured and has no actionable button.
type IntegrationAction =
  | { kind: "oauth"; href: string; label: string }
  | { kind: "external"; href: string; label: string }
  | null;

interface IntegrationRow {
  key: string;
  label: string;
  connected: boolean;
  webhook?: boolean; // Signable/Stripe/Xero webhook status, shown as a badge when connected
  warning?: string | null; // red badge for a connected-but-unhealthy state (e.g. Xero token expired)
  helper?: string; // setup hint shown under the action
  action: IntegrationAction;
  // Session ASB, Item 4: an OAuth connection the office can remove, and what stops when it goes (plain English).
  disconnect?: { provider: "google" | "xero"; consequence: string };
}

// Live integration status. OAuth providers (Google, Xero) can be (re)connected from
// here; the rest are API-key clients configured in the host service config.
function IntegrationsSection({ refreshKey }: { refreshKey: number }) {
  const [status, setStatus] = useState<IntegrationStatus | null>(null);
  const [state, setState] = useState<"loading" | "ready" | "error">("loading");
  const [syncing, setSyncing] = useState(false);
  const [syncMsg, setSyncMsg] = useState<string | null>(null);
  const [syncErr, setSyncErr] = useState<string | null>(null);
  // Session ASB, Item 4: disconnect (two-step branded confirm).
  const [disconnecting, setDisconnecting] = useState<{ provider: "google" | "xero"; label: string; consequence: string } | null>(null);
  const [disconnectBusy, setDisconnectBusy] = useState(false);
  const [disconnectErr, setDisconnectErr] = useState<string | null>(null);
  const [disconnectMsg, setDisconnectMsg] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  async function confirmDisconnect() {
    if (!disconnecting) return;
    setDisconnectBusy(true);
    setDisconnectErr(null);
    try {
      const res = await api.integrations.disconnect(disconnecting.provider);
      setDisconnectMsg(
        res.revokedAtProvider
          ? `${disconnecting.label} is disconnected, and its access has been removed at ${disconnecting.label} too.`
          : `${disconnecting.label} is disconnected here. ${disconnecting.label} did not confirm it removed the access, so remove it in your ${disconnecting.label} account settings as well.`,
      );
      setDisconnecting(null);
      setReloadKey((k) => k + 1);
    } catch (err) {
      setDisconnectErr(friendlyApiError(err, `Could not disconnect ${disconnecting.label}. Please try again.`));
    } finally {
      setDisconnectBusy(false);
    }
  }

  async function syncInvoices() {
    setSyncing(true);
    setSyncMsg(null);
    setSyncErr(null);
    try {
      const res = await api.billing.syncInvoices();
      setSyncMsg(`Synced ${res.ingested} invoice${res.ingested === 1 ? "" : "s"}. ${res.overdueCount} overdue.`);
    } catch (err) {
      setSyncErr(friendlyApiError(err, "Could not sync invoices. Please try again in a moment."));
    } finally {
      setSyncing(false);
    }
  }

  useEffect(() => {
    let live = true;
    api.integrations
      .status()
      .then((s) => live && (setStatus(s), setState("ready")))
      .catch(() => live && setState("error"));
    return () => {
      live = false;
    };
  }, [refreshKey, reloadKey]);

  const rows: IntegrationRow[] = status
    ? [
        {
          key: "google",
          label: "Google (Drive & Gmail)",
          connected: status.google.connected,
          action: { kind: "oauth", href: api.integrations.connectHref("google"), label: status.google.connected ? "Reconnect" : "Connect" },
          disconnect: status.google.connected
            ? {
                provider: "google",
                consequence:
                  "Emails, Drive filing and the weekly database backup to Drive will stop until you connect Google again.",
              }
            : undefined,
        },
        {
          key: "xero",
          label: "Xero",
          connected: status.xero.connected,
          // Session ADU: the webhook badge, same as Signable/Stripe. It reads "not set" (red) when
          // the Xero webhook secret is unconfigured, which is the honest state: the webhook route
          // 503s every delivery until XERO_WEBHOOK_SECRET is set. The daily reconcile still keeps
          // invoice data in sync, so this is advisory, not a connection failure.
          webhook: status.xero.webhookConfigured,
          // The worker refreshes the token proactively every 20 minutes, so this badge showing
          // at all means the refresh is failing: reconnect from the button below.
          warning: status.xero.connected && status.xero.tokenExpired ? "Token expired: reconnect" : null,
          action: { kind: "oauth", href: api.integrations.connectHref("xero"), label: status.xero.connected ? "Reconnect" : "Connect" },
          disconnect: status.xero.connected
            ? { provider: "xero", consequence: "Invoices will stop reaching Xero until you connect again." }
            : undefined,
        },
        {
          key: "signable",
          label: "Signable",
          connected: status.signable.configured,
          webhook: status.signable.webhookConfigured,
          action: status.signable.configured ? { kind: "external", href: "https://app.signable.co.uk", label: "View account" } : null,
        },
        {
          key: "stripe",
          label: "Stripe",
          connected: status.stripe.configured,
          webhook: status.stripe.webhookConfigured,
          action: status.stripe.configured ? { kind: "external", href: "https://dashboard.stripe.com", label: "Dashboard" } : null,
        },
        {
          key: "twilio",
          label: "Twilio SMS",
          connected: status.twilio.configured,
          action: status.twilio.configured ? { kind: "external", href: "https://console.twilio.com", label: "Console" } : null,
        },
        // The Google Maps card was retired: mapping is OpenStreetMap + Nominatim (a locked,
        // permanent decision, no API key). See the OpenStreetMap (Geocoding) card below.
      ]
    : [];

  // The section header's one-line state, from the status this card already fetched (SC7B).
  useReportSettingsSummary(rows.length > 0 ? `${rows.filter((r) => r.connected).length} of ${rows.length} integrations connected` : null);

  return (
    <section className="mt-5 rounded-ele border border-ele-purple-accent bg-ele-surface p-5">
      <h2 className="mb-3 font-heading text-lg text-ele-blue">Integrations</h2>
      {state === "loading" ? (
        <Loader shape="inline" />
      ) : state === "error" ? (
        <p role="alert" className="text-sm text-ele-strong">Could not load integration status.</p>
      ) : (
        <>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {rows.map((r) => (
              <div key={r.key} className="flex flex-col rounded-ele border border-ele-purple-accent bg-ele-surface p-3">
                {/* Integration status via the locked six-variant pill system (Session ACG). */}
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <span className="text-sm text-ele-strong">{r.label}</span>
                  <StatusPill word={r.connected ? "Connected" : "Not set"} tone={r.connected ? "green" : "red"} />
                </div>
                {r.warning && <StatusPill word={r.warning} tone="red" className="mt-2 w-fit" />}
                {r.connected && r.webhook !== undefined && (
                  <StatusPill word={r.webhook ? "webhook set" : "webhook not set"} tone={r.webhook ? "green" : "red"} className="mt-2 w-fit" />
                )}
                {r.action && r.action.kind === "oauth" && (
                  <Button size="sm" className="mt-2 w-fit" onClick={() => window.location.assign(r.action!.href)}>
                    {r.action.label}
                  </Button>
                )}
                {r.action && r.action.kind === "external" && (
                  <a
                    href={r.action.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-2 inline-block w-fit rounded-ele border border-ele-green px-3 py-1 font-menu text-xs tracking-wide text-ele-green transition hover:bg-ele-green hover:text-ele-bg"
                  >
                    {r.action.label}
                  </a>
                )}
                {r.disconnect && (
                  <button
                    type="button"
                    onClick={() => {
                      setDisconnectErr(null);
                      setDisconnectMsg(null);
                      setDisconnecting({ provider: r.disconnect!.provider, label: r.key === "google" ? "Google" : "Xero", consequence: r.disconnect!.consequence });
                    }}
                    className="mt-2 inline-block w-fit rounded-ele border border-ele-purple-accent px-3 py-1 font-menu text-xs tracking-wide text-ele-body transition hover:border-ele-danger hover:text-ele-danger"
                  >
                    Disconnect
                  </button>
                )}
                {r.helper && <p className="mt-2 text-xs text-ele-body">{r.helper}</p>}
              </div>
            ))}
            <GeocodingCard refreshKey={refreshKey} />
          </div>
          {disconnectMsg && (
            <p role="status" className="mt-3 text-sm text-ele-body">
              {disconnectMsg}
            </p>
          )}
          <ConfirmDeleteModal
            open={disconnecting !== null}
            variant="destructive"
            title={disconnecting ? `Disconnect ${disconnecting.label}?` : ""}
            message={disconnecting ? disconnecting.consequence : ""}
            confirmLabel="Disconnect"
            busy={disconnectBusy}
            error={disconnectErr}
            onConfirm={() => void confirmDisconnect()}
            onCancel={() => {
              if (disconnectBusy) return;
              setDisconnecting(null);
              setDisconnectErr(null);
            }}
          />

          <div className="mt-4 border-t border-ele-purple-accent pt-4">
            <Button size="sm" onClick={syncInvoices} disabled={syncing}>
              {syncing ? "Syncing..." : "Sync Xero Invoices"}
            </Button>
            <span className="ml-3 text-xs text-ele-body">Pull the latest overdue invoices from Xero now (this also runs daily).</span>
            {syncMsg && <p className="mt-2 text-sm text-ele-green">{syncMsg}</p>}
            {syncErr && <p role="alert" className="mt-2 text-sm text-ele-danger">{syncErr}</p>}
          </div>

          {/* Keyed on refreshKey: InboundCallWebhookCard only seeds its url state from initialUrl once
              on mount, so without a fresh key here a refresh-driven status refetch would leave it
              showing a stale token if the value had genuinely changed server-side. */}
          {status && <InboundCallWebhookCard key={refreshKey} initialUrl={status.inboundCall.webhookUrl} />}
        </>
      )}
    </section>
  );
}

// The copyable Claude instructions card lives in components/ConnectorInstructionsCard.tsx since Session CA1, so the
// Connected AI page renders the same component first under its heading. Settings keeps it in this admin-only section.

// The white-label inbound-call webhook token (Session ACY). A phone provider that POSTs inbound
// calls (Twilio, Vonage, Telnyx, ...) is given the URL below; each call it posts logs automatically.
// ELE's own provider has no such feed, so this stays off for ELE. Admin-only (inside IntegrationsSection).
// Exported for its own test (Session APV), as the Claude instructions card is.
export function InboundCallWebhookCard({ initialUrl }: { initialUrl: string | null }) {
  const [url, setUrl] = useState<string | null>(initialUrl);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  // Session APV: cleared if the card closes first (it is remounted on every refresh), and re-armed by a second copy.
  const resetCopiedLater = useSingleTimeout();

  async function generate() {
    setBusy(true);
    setErr(null);
    setCopied(false);
    try {
      const res = await api.integrations.generateInboundCallToken();
      setUrl(res.webhookUrl);
    } catch (e) {
      setErr(friendlyApiError(e, "Could not generate the webhook token. Please try again."));
    } finally {
      setBusy(false);
    }
  }

  async function revoke() {
    setBusy(true);
    setErr(null);
    setCopied(false);
    try {
      await api.integrations.revokeInboundCallToken();
      setUrl(null);
    } catch (e) {
      setErr(friendlyApiError(e, "Could not revoke the webhook token. Please try again."));
    } finally {
      setBusy(false);
    }
  }

  async function copy() {
    if (!url) return;
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      resetCopiedLater(() => setCopied(false), 2000);
    } catch {
      /* clipboard blocked; the URL is selectable in the field */
    }
  }

  return (
    <div className="mt-4 border-t border-ele-purple-accent pt-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="font-heading text-sm uppercase tracking-wide text-ele-strong">Inbound call webhook (white-label)</h3>
        <StatusPill word={url ? "Active" : "Not set"} tone={url ? "green" : "red"} />
      </div>
      <p className="mt-1 max-w-2xl text-xs text-ele-body">
        For a phone provider that posts inbound-call events (Twilio, Vonage, Telnyx and similar). Generate a token, give the
        provider the URL below, and each call it posts is logged automatically and matched to a customer by phone number. ELE's
        own provider has no such feed, so this can stay off.
      </p>
      {url ? (
        <>
          <label className="mt-3 block font-menu text-xs uppercase tracking-wide text-ele-body">Webhook URL (the provider POSTs here)</label>
          <div className="mt-1 flex flex-wrap items-center gap-2">
            <input
              readOnly
              value={url}
              onFocus={(e) => e.currentTarget.select()}
              aria-label="Inbound call webhook URL"
              className="min-w-0 flex-1 rounded-ele border border-ele-purple-accent bg-ele-field px-3 py-2 text-xs text-ele-strong"
            />
            <button
              type="button"
              onClick={copy}
              className="max-sm:min-h-11 rounded-ele border border-ele-blue px-3 py-2 font-menu text-xs tracking-wide text-ele-blue transition hover:bg-ele-blue hover:text-ele-bg"
            >
              {copied ? "Copied" : "Copy"}
            </button>
          </div>
          <p className="mt-2 max-w-2xl text-xs text-ele-body opacity-70">
            The generic adapter handles the common field names most providers use. For an unusual provider, add a named adapter
            in code and select it with a <code>?provider=name</code> query on the URL.
          </p>
          <div className="mt-3 flex flex-wrap gap-2">
            <button
              type="button"
              onClick={generate}
              disabled={busy}
              className="max-sm:min-h-11 rounded-ele border border-ele-purple-accent px-3 py-2 font-menu text-xs tracking-wide text-ele-body transition hover:text-ele-strong disabled:opacity-50"
            >
              {busy ? "Working..." : "Regenerate"}
            </button>
            <ConfirmButton
              onConfirm={revoke}
              busy={busy}
              label="Revoke"
              confirmLabel="Confirm revoke?"
              busyLabel="Revoking..."
              className="rounded-ele border border-ele-purple-accent px-3 py-2 font-menu text-xs tracking-wide text-ele-body transition hover:border-ele-danger hover:text-ele-danger"
            />
          </div>
        </>
      ) : (
        <Button size="sm" className="mt-3" onClick={generate} disabled={busy}>
          {busy ? "Generating..." : "Generate webhook token"}
        </Button>
      )}
      {err && <p role="alert" className="mt-2 text-sm text-ele-danger">{err}</p>}
    </div>
  );
}

// Mapping is OpenStreetMap + Nominatim (the locked, permanent decision -- no API key, no Google
// Maps). This card replaces the old Google Maps card: always active, with a "Geocode all sites"
// button (the same one-shot backfill as the map page) and a live count of located sites. Exported
// for a focused render test.
export function GeocodingCard({ refreshKey }: { refreshKey?: number } = {}) {
  const [status, setStatus] = useState<GeocodeStatus | null>(null);
  const [geocoding, setGeocoding] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    api.map
      .geocodeStatus()
      .then((s) => live && setStatus(s))
      .catch(() => undefined);
    return () => {
      live = false;
    };
  }, [refreshKey]);

  async function run() {
    setGeocoding(true);
    setMsg(null);
    try {
      const summary = await api.map.geocodeAll();
      try {
        setStatus(await api.map.geocodeStatus());
      } catch {
        // The count is best-effort; the summary below is the authoritative result.
      }
      if (summary.queued === 0) {
        setMsg("All sites are already located.");
      } else {
        const sitesWord = summary.queued === 1 ? "site" : "sites";
        const failedNote = summary.failed > 0 ? ` ${summary.failed} could not be located.` : "";
        setMsg(`${summary.succeeded} of ${summary.queued} ${sitesWord} geocoded.${failedNote}`);
      }
    } catch (err) {
      const detail = friendlyApiError(err, "");
      setMsg(`Could not geocode sites${detail ? `: ${detail}` : ""}. Please try again in a moment.`);
    } finally {
      setGeocoding(false);
    }
  }

  return (
    <div className="flex flex-col rounded-ele border border-ele-purple-accent bg-ele-surface p-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="text-sm text-ele-strong">OpenStreetMap (Geocoding)</span>
        <StatusPill word="Active" tone="green" />
      </div>
      <p className="mt-2 text-xs text-ele-body">Site addresses are geocoded automatically using Nominatim. No API key required.</p>
      <Button size="sm" className="mt-2 w-fit" onClick={() => void run()} disabled={geocoding}>
        {geocoding ? "Geocoding..." : "Geocode all sites"}
      </Button>
      {status && (
        <p className="mt-2 text-xs text-ele-body">
          {status.geocoded} of {status.total} {status.total === 1 ? "site" : "sites"} geocoded
        </p>
      )}
      {msg && <p role="status" className="mt-1 text-xs text-ele-green">{msg}</p>}
    </div>
  );
}
